// Phone helpers built on libphonenumber-js. Everything is keyed off an ISO
// 3166-1 alpha-2 country code (the same code the geo SSOT stores), so the
// supported phone countries are exactly the supported markets — no separate
// dial-code or mask table to maintain. Shared across the web frontends; import
// via "@osmix/lib/phone".
import {
  AsYouType,
  type CountryCode,
  getCountryCallingCode,
  getExampleNumber,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
} from "libphonenumber-js"
import examples from "libphonenumber-js/examples.mobile.json"

/** Max national digits for a country, taken from a canonical example number.
 *  validatePhoneNumberLength is too loose here (RU reports INVALID_LENGTH, not
 *  TOO_LONG, for 11–15 digits), so the example length gives the real cap. */
const maxNationalLength = (country: CountryCode): number | undefined => {
  try {
    return getExampleNumber(country, examples)?.nationalNumber.length
  } catch {
    return undefined
  }
}

/** "+7", "+374", … for a country code. Empty string if the code is unknown. */
export const dialCode = (iso: string): string => {
  try {
    return `+${getCountryCallingCode(iso.toUpperCase() as CountryCode)}`
  } catch {
    return ""
  }
}

/** 🇷🇺 from "RU" — regional-indicator letters. */
export const flagEmoji = (iso: string): string =>
  iso
    .toUpperCase()
    .replace(/[^A-Z]/g, "")
    .replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)))

/** Format the national part as the user types, for the selected country.
 *  Caps the input at the country's national length — AsYouType keeps appending
 *  digits past the template otherwise, letting users type forever. */
export const formatNational = (input: string, iso: string): string => {
  let digits = input.replace(/\D/g, "")
  if (!digits) return ""
  const country = iso.toUpperCase() as CountryCode
  const max = maxNationalLength(country)
  if (max) digits = digits.slice(0, max)
  try {
    return new AsYouType(country).input(digits)
  } catch {
    return digits
  }
}

/** Build the canonical E.164 string for the typed national number, or "" when
 *  nothing has been entered. Not necessarily valid yet — validate separately. */
export const toE164 = (input: string, iso: string): string => {
  const digits = input.replace(/\D/g, "")
  if (!digits) return ""
  const dc = dialCode(iso)
  return dc ? `${dc}${digits}` : ""
}

/** Whether a full E.164 string is a valid number. */
export const isValidPhone = (e164: string): boolean => {
  if (!e164) return false
  try {
    return isValidPhoneNumber(e164)
  } catch {
    return false
  }
}

/** Pretty E.164 for read-only display, e.g. "+7 999 999-99-99" — the same mask
 *  the input applies, prefixed with the dial code. Falls back to the raw string
 *  when it can't be parsed. */
export const formatDisplay = (e164: string): string => {
  const parsed = e164 ? parsePhoneNumberFromString(e164) : undefined
  if (!parsed?.country) return e164
  return `${dialCode(parsed.country)} ${formatNational(parsed.nationalNumber, parsed.country)}`
}

/** Split an E.164 string back into a country + national display, to seed the
 *  input from a stored value. Falls back to the given default country. */
export const fromE164 = (
  e164: string,
  defaultCountry: string,
): { country: string; national: string } => {
  const parsed = e164 ? parsePhoneNumberFromString(e164) : undefined
  if (parsed?.country) {
    return { country: parsed.country, national: formatNational(parsed.nationalNumber, parsed.country) }
  }
  return { country: defaultCountry, national: "" }
}
